import axios from 'axios';
import { API_URL } from '../config/api';

/**
 * Client Service
 * 
 * API calls for client portal features
 */

const API_BASE = API_URL + '/client';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

const clientService = {
  
  // Dashboard
  getMyPipelines: async () => {
    const response = await axios.get(`${API_BASE}/pipelines`, getAuthHeaders());
    return response.data;
  },

  getDashboardStats: async () => {
    const response = await axios.get(`${API_BASE}/dashboard/stats`, getAuthHeaders());
    return response.data;
  },

  // Pipeline View
  getPipeline: async (pipelineId) => {
    const response = await axios.get(`${API_BASE}/pipelines/${pipelineId}`, getAuthHeaders());
    return response.data;
  },

  // Toggle pipeline completion status
  togglePipelineCompletion: async (pipelineId) => {
    const response = await axios.put(`${API_BASE}/pipelines/${pipelineId}/toggle-completion`, {}, getAuthHeaders());
    return response.data;
  },

  // NEW: Delete pipeline
  deletePipeline: async (pipelineId) => {
    const response = await axios.delete(`${API_BASE}/pipelines/${pipelineId}`, getAuthHeaders());
    return response.data;
  },

  // Candidate Rating & Feedback
  rateCandidate: async (cardId, rating, feedback) => {
    const response = await axios.post(`${API_BASE}/cards/${cardId}/rate`, {
      rating,
      feedback
    }, getAuthHeaders());
    return response.data;
  },

  updateCandidateStatus: async (cardId, status, rejection_reason) => {
    const response = await axios.put(`${API_BASE}/cards/${cardId}/status`, {
      status, rejection_reason
    }, getAuthHeaders());
    return response.data;
  },

  // Interview Stages
  moveCandidateToStage: async (cardId, stageId) => {
    const response = await axios.put(`${API_BASE}/cards/${cardId}/stage`, {
      client_stage_id: stageId
    }, getAuthHeaders());
    return response.data;
  },

  createInterviewStage: async (pipelineId, name, description) => {
    const response = await axios.post(`${API_BASE}/pipelines/${pipelineId}/stages`, {
      name,
      description
    }, getAuthHeaders());
    return response.data;
  },

  getInterviewStages: async (pipelineId) => {
    const response = await axios.get(`${API_BASE}/pipelines/${pipelineId}/stages`, getAuthHeaders());
    return response.data;
  },

  // Documents
  getCandidateDocuments: async (cardId) => {
    const response = await axios.get(`${API_BASE}/cards/${cardId}/documents`, getAuthHeaders());
    return response.data;
  },

  // Notes
  getCandidateNotes: async (cardId) => {
    const response = await axios.get(`${API_BASE}/cards/${cardId}/notes`, getAuthHeaders());
    return response.data;
  },

  addNote: async (cardId, noteText) => {
    const response = await axios.post(`${API_BASE}/cards/${cardId}/notes`, {
      note_text: noteText,
      is_private: true
    }, getAuthHeaders());
    return response.data;
  },

  // Update interview stage
  updateInterviewStage: async (stageId, name, position) => {
    const response = await axios.put(`${API_BASE}/stages/${stageId}`, {
      name,
      position
    }, getAuthHeaders());
    return response.data;
  },

  // Delete interview stage
  deleteInterviewStage: async (stageId) => {
    const response = await axios.delete(`${API_BASE}/stages/${stageId}`, getAuthHeaders());
    return response.data;
  },

  // Stage-specific feedback
  getStageFeedback: async (cardId) => {
    const response = await axios.get(`${API_BASE}/cards/${cardId}/stage-feedback`, getAuthHeaders());
    return response.data;
  },

  addStageFeedback: async (cardId, stageId, feedbackText, interviewerName, rating, meetingId) => {
    const response = await axios.post(
        `${API_BASE}/cards/${cardId}/stage-feedback`,
        {
            stage_id: stageId,
            feedback_text: feedbackText,
            interviewer_name: interviewerName,
            rating: rating,
            meeting_id: meetingId || null
        },
        getAuthHeaders()
    );
    return response.data;
  },

  // NEW: Add candidate to pipeline
  addCandidateToPipeline: async (pipelineId, candidateData) => {
    const response = await axios.post(
      `${API_BASE}/pipelines/${pipelineId}/candidates`,
      candidateData,
      getAuthHeaders()
    );
    return response.data;
  },

  // NEW: Create standalone candidate
  createCandidate: async (candidateData) => {
    const response = await axios.post(
      `${API_BASE}/candidates`,
      candidateData,
      getAuthHeaders()
    );
    return response.data;
  },

  // NEW: Add existing candidate to pipeline
  addExistingCandidateToPipeline: async (candidateId, pipelineId, stageId = null) => {
    const response = await axios.post(
      `${API_BASE}/candidates/${candidateId}/add-to-pipeline`,
      { 
        pipeline_id: pipelineId,
        stage_id: stageId
      },
      getAuthHeaders()
    );
    return response.data;
  },

  // NEW: Get candidates that have been presented to this client's firm
  // Only returns candidates that were sent to the client (not entire database)
  getPresentedCandidates: async () => {
    const response = await axios.get(
      `${API_BASE}/candidates`,
      getAuthHeaders()
    );
    return response.data;
  },

};

export default clientService;
